import { useEffect, useRef } from 'react';

/**
 * Custom hook to observe when an element is in view using the Intersection Observer API.
 * Triggers the provided callback when the element is in view.
 *
 * @param ref the ref of the element to observe
 * @param callback the callback to trigger when the element is in view
 * @param options the options for the intersection observer (optional, defaults to { threshold: 0.1 })
 */

export const useIntersectionObserver = (
  ref: React.RefObject<HTMLElement>,
  callback: () => void,
  options: IntersectionObserverInit = { threshold: 0.1 },
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        callback();
      }
    }, options);

    if (ref.current) observer.observe(ref.current);
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [ref, callback, options]);
};
